
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import UserInfo from "./AllAvailabilities";

import {
  getOtherMedicalAvailability,
  saveOtherMedicalAvailability,
  updateSingleDay,
  updateSingleSlot,
  clearWeek,
  deleteAvailability,
} from "../../redux/apis/Availability/otherMedicalAvailabilitySlice";

// ============================================================
// CONFIG
// ============================================================

const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const defaultSessions = [
  {
    name: "First Half",
    startTime: "09:00",
    endTime: "13:00",
    isOpen: true,
  },
  {
    name: "Second Half",
    startTime: "14:00",
    endTime: "18:00",
    isOpen: true,
  },
];

// ============================================================
// COLORS
// ============================================================

const CUSTOM_GRADIENT =
  "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)";

const BUTTON_GRADIENT =
  "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))";

// ============================================================
// DATE HELPERS
// ============================================================

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getToday = () => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return today;
};

const getDayName = (dateString) => {
  if (!dateString) {
    return "";
  }

  const date = new Date(`${dateString}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return days[date.getDay()];
};

// ============================================================
// WEEK
// ============================================================

const getWeek = () => {
  const base = getToday();

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(base);

    date.setDate(base.getDate() + index);

    return {
      day: days[date.getDay()],
      date: formatDate(date),
      isHoliday: false,

      sessions: defaultSessions.map((session) => ({
        ...session,
      })),
    };
  });
};

// ============================================================
// NORMALIZE API DATA
// ============================================================

const normalizeAvailability = (availability) => {
  if (!Array.isArray(availability)) {
    return [];
  }

  return availability.map((item) => {
    const timingMap = {};

    if (Array.isArray(item?.openingTimings)) {
      item.openingTimings.forEach((timing) => {
        if (timing?.name) {
          timingMap[timing.name] = timing;
        }
      });
    }

    return {
      day:
        item?.day ||
        getDayName(item?.date) ||
        "",

      date: item?.date || "",

      isHoliday: Boolean(item?.isHoliday),

      sessions: defaultSessions.map((defaultSession) => {
        const savedSession =
          timingMap[defaultSession.name];

        if (!savedSession) {
          return {
            ...defaultSession,
            isOpen: false,
          };
        }

        return {
          ...defaultSession,

          startTime:
            savedSession.startTime ||
            defaultSession.startTime,

          endTime:
            savedSession.endTime ||
            defaultSession.endTime,

          isOpen: true,
        };
      }),
    };
  });
};

// ============================================================
// COMPONENT
// ============================================================

const OtherMedicalAvailability = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ==========================================================
  // REDUX
  // ==========================================================

  const availability = useSelector(
    (state) =>
      state.otherMedicalAvailability?.availability || [],
  );

  const loading = useSelector(
    (state) =>
      state.otherMedicalAvailability?.loading || false,
  );

  const error = useSelector(
    (state) =>
      state.otherMedicalAvailability?.error || null,
  );

  const token = useSelector(
    (state) => state.auth?.token,
  );

  // ==========================================================
  // LOCAL STATE
  // ==========================================================

  const [weekly, setWeekly] = useState(getWeek());

  const [savingSlot, setSavingSlot] = useState(null);

  const [savingDay, setSavingDay] = useState(null);

  const [savingWeek, setSavingWeek] = useState(false);

  const [clearingWeek, setClearingWeek] = useState(false);

  const [deletingAll, setDeletingAll] = useState(false);

  // ==========================================================
  // FETCH AVAILABILITY
  // ==========================================================

  useEffect(() => {
    if (!token) {
      return;
    }

    dispatch(
      getOtherMedicalAvailability({
        token,
      }),
    );
  }, [token, dispatch]);

  // ==========================================================
  // MAP API DATA
  // ==========================================================

  useEffect(() => {
    if (!Array.isArray(availability)) {
      return;
    }

    if (availability.length === 0) {
      return;
    }

    const mapped =
      normalizeAvailability(availability);

    if (mapped.length > 0) {
      setWeekly(mapped);
    }
  }, [availability]);

  // ==========================================================
  // BACK BUTTON
  // ==========================================================

  const handleBack = () => {
    navigate(-1);
  };

  // ==========================================================
  // UPDATE DATE
  // ==========================================================

  const updateDate = (index, value) => {
    setWeekly((previous) =>
      previous.map((day, dayIndex) => {
        if (dayIndex !== index) {
          return day;
        }

        return {
          ...day,

          date: value,

          day:
            getDayName(value) ||
            day.day,
        };
      }),
    );
  };

  // ==========================================================
  // UPDATE SESSION
  // ==========================================================

  const updateSession = (
    dayIndex,
    sessionIndex,
    field,
    value,
  ) => {
    setWeekly((previous) =>
      previous.map((day, currentDayIndex) => {
        if (currentDayIndex !== dayIndex) {
          return day;
        }

        return {
          ...day,

          sessions: day.sessions.map(
            (session, currentSessionIndex) => {
              if (
                currentSessionIndex !==
                sessionIndex
              ) {
                return session;
              }

              return {
                ...session,
                [field]: value,
              };
            },
          ),
        };
      }),
    );
  };

  // ==========================================================
  // TOGGLE SESSION
  // ==========================================================

  const toggleSession = (
    dayIndex,
    sessionIndex,
  ) => {
    setWeekly((previous) =>
      previous.map((day, currentDayIndex) => {
        if (currentDayIndex !== dayIndex) {
          return day;
        }

        return {
          ...day,

          sessions: day.sessions.map(
            (session, currentSessionIndex) => {
              if (
                currentSessionIndex !==
                sessionIndex
              ) {
                return session;
              }

              return {
                ...session,
                isOpen: !session.isOpen,
              };
            },
          ),
        };
      }),
    );
  };

  // ==========================================================
  // TOGGLE HOLIDAY
  // ==========================================================

  const toggleHoliday = (index) => {
    setWeekly((previous) =>
      previous.map((day, dayIndex) => {
        if (dayIndex !== index) {
          return day;
        }

        const newHoliday = !day.isHoliday;

        return {
          ...day,

          isHoliday: newHoliday,

          sessions: newHoliday
            ? day.sessions.map((session) => ({
                ...session,
                isOpen: false,
              }))
            : day.sessions.map((session) => ({
                ...session,
              })),
        };
      }),
    );
  };

  // ==========================================================
  // VALIDATE SESSION
  // ==========================================================

  const validateSession = (session) => {
    if (!session?.isOpen) {
      return true;
    }

    if (
      !session.startTime ||
      !session.endTime
    ) {
      alert(
        `❌ Start time and end time are required for ${session.name}.`,
      );

      return false;
    }

    if (
      session.startTime >=
      session.endTime
    ) {
      alert(
        `❌ Start time must be before end time for ${session.name}.`,
      );

      return false;
    }

    return true;
  };

  // ==========================================================
  // VALIDATE DAY
  // ==========================================================

  const validateDay = (day) => {
    if (!day?.date) {
      alert("❌ Date is required.");

      return false;
    }

    if (day.isHoliday) {
      return true;
    }

    for (const session of day.sessions) {
      if (!validateSession(session)) {
        return false;
      }
    }

    return true;
  };

  // ==========================================================
  // CREATE DAY PAYLOAD
  // ==========================================================

  const createDayPayload = (day) => {
    return {
      day: day.day,

      date: day.date,

      isHoliday: Boolean(day.isHoliday),

      openingTimings: day.isHoliday
        ? []
        : day.sessions
            .filter(
              (session) =>
                session.isOpen,
            )
            .map((session) => ({
              name: session.name,
              startTime:
                session.startTime,
              endTime:
                session.endTime,
            })),
    };
  };

  // ==========================================================
  // SAVE WEEK
  // ==========================================================

  const saveWeek = async () => {
    if (!token) {
      alert(
        "❌ Authentication token missing.",
      );

      return;
    }

    if (savingWeek) {
      return;
    }

    for (const day of weekly) {
      if (!validateDay(day)) {
        return;
      }
    }

    const dates = weekly.map(
      (day) => day.date,
    );

    if (
      new Set(dates).size !==
      dates.length
    ) {
      alert(
        "❌ Duplicate dates found.",
      );

      return;
    }

    const payload = {
      availability:
        weekly.map(createDayPayload),

      timezone:
        Intl.DateTimeFormat().resolvedOptions()
          .timeZone,

      token,
    };

    setSavingWeek(true);

    try {
      console.log(
        "💾 SAVE WEEK PAYLOAD:",
        payload,
      );

      await dispatch(
        saveOtherMedicalAvailability(
          payload,
        ),
      ).unwrap();

      alert(
        "✅ Week availability saved successfully.",
      );

      dispatch(
        getOtherMedicalAvailability({
          token,
        }),
      );
    } catch (err) {
      console.error(
        "❌ Save week error:",
        err,
      );

      const message =
        err?.message ||
        err?.payload?.message ||
        "Failed to save week availability.";

      alert(`❌ ${message}`);
    } finally {
      setSavingWeek(false);
    }
  };

  // ==========================================================
  // SAVE DAY
  // ==========================================================

  const saveDay = async (day, index) => {
    if (!token) {
      alert(
        "❌ Authentication token missing.",
      );

      return;
    }

    if (!validateDay(day)) {
      return;
    }

    if (savingDay === index) {
      return;
    }

    setSavingDay(index);

    try {
      const payload = {
        ...createDayPayload(day),
        token,
      };

      console.log(
        "💾 UPDATE SINGLE DAY:",
        payload,
      );

      await dispatch(
        updateSingleDay(payload),
      ).unwrap();

      alert(
        `✅ ${day.day} availability saved.`,
      );

      dispatch(
        getOtherMedicalAvailability({
          token,
        }),
      );
    } catch (err) {
      console.error(
        "❌ Save day error:",
        err,
      );

      const message =
        err?.message ||
        err?.payload?.message ||
        "Failed to save day.";

      alert(`❌ ${message}`);
    } finally {
      setSavingDay(null);
    }
  };

  // ==========================================================
  // SAVE SLOT
  // ==========================================================

  const saveSlot = async (
    day,
    session,
  ) => {
    if (!token) {
      alert(
        "❌ Authentication token missing.",
      );

      return;
    }

    if (!day?.date) {
      alert("❌ Date is required.");

      return;
    }

    if (!session?.isOpen) {
      alert(
        "❌ Please open this slot before saving.",
      );

      return;
    }

    if (
      !session.startTime ||
      !session.endTime
    ) {
      alert(
        "❌ Start time and end time are required.",
      );

      return;
    }

    if (
      session.startTime >=
      session.endTime
    ) {
      alert(
        "❌ Start time must be before end time.",
      );

      return;
    }

    const slotKey =
      `${day.date}-${session.name}`;

    if (savingSlot === slotKey) {
      return;
    }

    setSavingSlot(slotKey);

    try {
      const payload = {
        date: day.date,

        day: day.day,

        name: session.name,

        startTime:
          session.startTime,

        endTime:
          session.endTime,

        isOpen: true,

        token,
      };

      console.log(
        "💾 UPDATE SINGLE SLOT:",
        payload,
      );

      await dispatch(
        updateSingleSlot(payload),
      ).unwrap();

      alert(
        `✅ ${session.name} saved successfully.`,
      );

      setSavingSlot(null);

      dispatch(
        getOtherMedicalAvailability({
          token,
        }),
      );
    } catch (err) {
      console.error(
        "❌ Save slot error:",
        err,
      );

      const message =
        err?.message ||
        err?.payload?.message ||
        err?.error?.message ||
        "Failed to save slot.";

      alert(`❌ ${message}`);

      setSavingSlot(null);
    }
  };

  // ==========================================================
  // CLEAR WEEK
  // ==========================================================

  const clearWeekHandler = async () => {
    if (!token) {
      alert(
        "❌ Authentication token missing.",
      );

      return;
    }

    if (clearingWeek) {
      return;
    }

    const confirmed =
      window.confirm(
        "⚠️ Clear this week's availability?",
      );

    if (!confirmed) {
      return;
    }

    setClearingWeek(true);

    try {
      await dispatch(
        clearWeek({
          token,
        }),
      ).unwrap();

      setWeekly(getWeek());

      alert(
        "✅ Week availability cleared.",
      );

      dispatch(
        getOtherMedicalAvailability({
          token,
        }),
      );
    } catch (err) {
      console.error(
        "❌ Clear week error:",
        err,
      );

      const message =
        err?.message ||
        err?.payload?.message ||
        "Failed to clear week.";

      alert(`❌ ${message}`);
    } finally {
      setClearingWeek(false);
    }
  };

  // ==========================================================
  // DELETE ALL
  // ==========================================================

  const deleteAllHandler = async () => {
    if (!token) {
      alert(
        "❌ Authentication token missing.",
      );

      return;
    }

    if (deletingAll) {
      return;
    }

    const confirmed =
      window.confirm(
        "⚠️ Delete ALL medical availability?",
      );

    if (!confirmed) {
      return;
    }

    setDeletingAll(true);

    try {
      await dispatch(
        deleteAvailability({
          token,
        }),
      ).unwrap();

      setWeekly(getWeek());

      alert(
        "✅ All availability deleted.",
      );

      dispatch(
        getOtherMedicalAvailability({
          token,
        }),
      );
    } catch (err) {
      console.error(
        "❌ Delete all error:",
        err,
      );

      const message =
        err?.message ||
        err?.payload?.message ||
        "Failed to delete availability.";

      alert(`❌ ${message}`);
    } finally {
      setDeletingAll(false);
    }
  };

  // ==========================================================
  // BUTTON STYLE
  // ==========================================================

  const gradientButtonStyle = {
    backgroundImage: BUTTON_GRADIENT,
  };

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div className="min-h-screen bg-slate-100">
      {/* ======================================================
          TOP HEADER
      ====================================================== */}

      <div
        className="sticky top-0 z-40 border-b border-white/10 shadow-lg"
        style={{
          backgroundImage: CUSTOM_GRADIENT,
        }}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            {/* BACK */}

            <button
              type="button"
              onClick={handleBack}
              className="
                inline-flex
                shrink-0
                items-center
                gap-2
                rounded-xl
                border
                border-white/20
                bg-white/10
                px-3
                py-2
                text-sm
                font-semibold
                text-white
                backdrop-blur-sm
                transition
                hover:bg-white/20
                active:scale-95
              "
            >
              <span className="text-lg">
                ←
              </span>

              <span className="hidden sm:inline">
                Back
              </span>
            </button>

            {/* TITLE */}

            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-white sm:text-2xl">
                🏥 Medical Availability
              </h1>

              <p className="hidden text-xs text-sky-100 sm:block">
                Manage working hours, holidays
                and appointment availability
              </p>
            </div>
          </div>

          {/* STATUS */}

          <div
            className="
              hidden
              items-center
              gap-2
              rounded-full
              border
              border-white/20
              bg-white/10
              px-3
              py-1.5
              text-xs
              font-semibold
              text-white
              sm:flex
            "
          >
            <span className="h-2 w-2 rounded-full bg-emerald-400" />

            Scheduling
          </div>
        </div>
      </div>

      {/* ======================================================
          CONTENT
      ====================================================== */}

      <div className="mx-auto max-w-7xl p-4 sm:p-6">
        {/* ====================================================
            USER INFO
        ==================================================== */}

        <div
          className="
            mb-6
            overflow-hidden
            rounded-2xl
            border
            border-slate-200
            bg-white
            shadow-sm
          "
        >
          <div
            className="h-1.5"
            style={{
              backgroundImage: BUTTON_GRADIENT,
            }}
          />

          <div className="p-4">
            <UserInfo />
          </div>
        </div>

        {/* ====================================================
            ERROR
        ==================================================== */}

        {error && (
          <div
            className="
              mb-5
              rounded-xl
              border
              border-red-200
              bg-red-50
              px-4
              py-3
              text-sm
              font-medium
              text-red-700
            "
          >
            ❌ {error}
          </div>
        )}

        {/* ====================================================
            TOP ACTIONS
        ==================================================== */}

        <div
          className="
            mb-6
            flex
            flex-wrap
            items-center
            justify-between
            gap-3
            rounded-2xl
            border
            border-slate-200
            bg-white
            p-4
            shadow-sm
          "
        >
          <div>
            <h2 className="text-base font-bold text-slate-800">
              Weekly Availability
            </h2>

            <p className="mt-0.5 text-xs text-slate-500">
              Configure your medical working
              schedule.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {/* SAVE WEEK */}

            <button
              type="button"
              onClick={saveWeek}
              disabled={savingWeek}
              style={
                !savingWeek
                  ? gradientButtonStyle
                  : undefined
              }
              className="
                rounded-xl
                px-4
                py-2.5
                text-sm
                font-bold
                text-white
                shadow-sm
                transition
                hover:-translate-y-0.5
                hover:shadow-md
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              {savingWeek
                ? "Saving Week..."
                : "💾 Save Week"}
            </button>

            {/* CLEAR WEEK */}

            <button
              type="button"
              onClick={clearWeekHandler}
              disabled={clearingWeek}
              className="
                rounded-xl
                border
                border-amber-200
                bg-amber-50
                px-4
                py-2.5
                text-sm
                font-bold
                text-amber-700
                transition
                hover:bg-amber-100
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              {clearingWeek
                ? "Clearing..."
                : "🧹 Clear Week"}
            </button>

            {/* DELETE ALL */}

            <button
              type="button"
              onClick={deleteAllHandler}
              disabled={deletingAll}
              className="
                rounded-xl
                border
                border-red-200
                bg-red-50
                px-4
                py-2.5
                text-sm
                font-bold
                text-red-700
                transition
                hover:bg-red-100
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              {deletingAll
                ? "Deleting..."
                : "🗑️ Delete All"}
            </button>
          </div>
        </div>

        {/* ====================================================
            WEEK
        ==================================================== */}

        <div className="space-y-5">
          {weekly.map((day, dayIndex) => (
            <div
              key={`${day.date}-${dayIndex}`}
              className="
                overflow-hidden
                rounded-2xl
                border
                border-slate-200
                bg-white
                shadow-sm
                transition
                hover:shadow-md
              "
            >
              {/* ==================================================
                  DAY HEADER
              ================================================== */}

              <div
                className="border-b border-white/10 p-4 sm:p-5"
                style={{
                  backgroundImage: CUSTOM_GRADIENT,
                }}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <div
                        className="
                          flex
                          h-10
                          w-10
                          items-center
                          justify-center
                          rounded-xl
                          bg-white/10
                          text-lg
                          shadow-inner
                        "
                      >
                        📅
                      </div>

                      <div>
                        <h2 className="text-lg font-bold text-white">
                          {day.day}
                        </h2>

                        <p className="text-xs text-sky-100">
                          Medical availability
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* DATE */}

                    <input
                      type="date"
                      value={day.date}
                      min={formatDate(
                        getToday(),
                      )}
                      onChange={(event) =>
                        updateDate(
                          dayIndex,
                          event.target.value,
                        )
                      }
                      className="
                        rounded-xl
                        border
                        border-white/20
                        bg-white
                        px-3
                        py-2
                        text-sm
                        font-medium
                        text-slate-700
                        outline-none
                        transition
                        focus:border-sky-400
                        focus:ring-2
                        focus:ring-sky-200
                      "
                    />

                    {/* WORKING */}

                    <button
                      type="button"
                      onClick={() => {
                        if (
                          day.isHoliday
                        ) {
                          toggleHoliday(
                            dayIndex,
                          );
                        }
                      }}
                      className={`
                        inline-flex
                        items-center
                        gap-2
                        rounded-xl
                        border
                        px-4
                        py-2
                        text-sm
                        font-bold
                        transition

                        ${
                          !day.isHoliday
                            ? `
                              border-emerald-300
                              bg-emerald-500/20
                              text-white
                              shadow-sm
                            `
                            : `
                              border-white/20
                              bg-white/10
                              text-sky-100
                              hover:bg-white/20
                            `
                        }
                      `}
                    >
                      <span
                        className={`
                          h-2.5
                          w-2.5
                          rounded-full
                          ${
                            !day.isHoliday
                              ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]"
                              : "bg-slate-400"
                          }
                        `}
                      />

                      Working
                    </button>

                    {/* HOLIDAY */}

                    <button
                      type="button"
                      onClick={() => {
                        if (
                          !day.isHoliday
                        ) {
                          toggleHoliday(
                            dayIndex,
                          );
                        }
                      }}
                      className={`
                        inline-flex
                        items-center
                        gap-2
                        rounded-xl
                        border
                        px-4
                        py-2
                        text-sm
                        font-bold
                        transition

                        ${
                          day.isHoliday
                            ? `
                              border-red-300
                              bg-red-500/20
                              text-white
                            `
                            : `
                              border-white/20
                              bg-white/10
                              text-sky-100
                              hover:bg-white/20
                            `
                        }
                      `}
                    >
                      <span
                        className={`
                          h-2.5
                          w-2.5
                          rounded-full
                          ${
                            day.isHoliday
                              ? "bg-red-400"
                              : "bg-slate-400"
                          }
                        `}
                      />

                      Holiday
                    </button>
                  </div>
                </div>
              </div>

              {/* ==================================================
                  HOLIDAY
              ================================================== */}

              {day.isHoliday && (
                <div className="p-5">
                  <div
                    className="
                      rounded-xl
                      border
                      border-red-200
                      bg-red-50
                      p-4
                    "
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="
                          flex
                          h-9
                          w-9
                          shrink-0
                          items-center
                          justify-center
                          rounded-lg
                          bg-red-100
                        "
                      >
                        🚫
                      </div>

                      <div>
                        <p className="font-bold text-red-700">
                          Holiday / Day Off
                        </p>

                        <p className="mt-1 text-sm text-red-600">
                          This day is marked as a
                          holiday. All appointment
                          sessions are closed.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() =>
                        saveDay(
                          day,
                          dayIndex,
                        )
                      }
                      disabled={
                        savingDay ===
                        dayIndex
                      }
                      style={
                        savingDay !==
                        dayIndex
                          ? gradientButtonStyle
                          : undefined
                      }
                      className="
                        rounded-xl
                        px-5
                        py-2.5
                        font-bold
                        text-white
                        shadow-sm
                        transition
                        hover:-translate-y-0.5
                        hover:shadow-md
                        disabled:cursor-not-allowed
                        disabled:opacity-50
                      "
                    >
                      {savingDay ===
                      dayIndex
                        ? "Saving Day..."
                        : "💾 Save Day"}
                    </button>
                  </div>
                </div>
              )}

              {/* ==================================================
                  WORKING DAY
              ================================================== */}

              {!day.isHoliday && (
                <div className="p-4 sm:p-5">
                  <div className="space-y-3">
                    {day.sessions.map(
                      (
                        session,
                        sessionIndex,
                      ) => {
                        const slotKey =
                          `${day.date}-${session.name}`;

                        const isSaving =
                          savingSlot ===
                          slotKey;

                        return (
                          <div
                            key={slotKey}
                            className="
                              rounded-2xl
                              border
                              border-slate-200
                              bg-slate-50
                              p-4
                              transition
                              hover:border-sky-200
                              hover:bg-sky-50/30
                            "
                          >
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                              {/* SESSION */}

                              <div className="lg:w-44">
                                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                  Session
                                </label>

                                <div className="font-bold text-slate-800">
                                  {session.name}
                                </div>

                                <div
                                  className={`
                                    mt-1
                                    text-xs
                                    font-bold
                                    ${
                                      session.isOpen
                                        ? "text-emerald-600"
                                        : "text-red-500"
                                    }
                                  `}
                                >
                                  {session.isOpen
                                    ? "● Available"
                                    : "● Closed"}
                                </div>
                              </div>

                              {/* START */}

                              <div>
                                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                  Start Time
                                </label>

                                <input
                                  type="time"
                                  value={
                                    session.startTime
                                  }
                                  onChange={(
                                    event,
                                  ) =>
                                    updateSession(
                                      dayIndex,
                                      sessionIndex,
                                      "startTime",
                                      event
                                        .target
                                        .value,
                                    )
                                  }
                                  disabled={
                                    !session.isOpen
                                  }
                                  className="
                                    rounded-xl
                                    border
                                    border-slate-300
                                    bg-white
                                    px-3
                                    py-2.5
                                    text-sm
                                    font-medium
                                    text-slate-700
                                    outline-none
                                    transition
                                    focus:border-sky-500
                                    focus:ring-2
                                    focus:ring-sky-100
                                    disabled:cursor-not-allowed
                                    disabled:bg-slate-100
                                    disabled:opacity-50
                                  "
                                />
                              </div>

                              {/* ARROW */}

                              <div className="hidden pb-3 text-lg font-bold text-sky-500 lg:block">
                                →
                              </div>

                              {/* END */}

                              <div>
                                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                  End Time
                                </label>

                                <input
                                  type="time"
                                  value={
                                    session.endTime
                                  }
                                  onChange={(
                                    event,
                                  ) =>
                                    updateSession(
                                      dayIndex,
                                      sessionIndex,
                                      "endTime",
                                      event
                                        .target
                                        .value,
                                    )
                                  }
                                  disabled={
                                    !session.isOpen
                                  }
                                  className="
                                    rounded-xl
                                    border
                                    border-slate-300
                                    bg-white
                                    px-3
                                    py-2.5
                                    text-sm
                                    font-medium
                                    text-slate-700
                                    outline-none
                                    transition
                                    focus:border-sky-500
                                    focus:ring-2
                                    focus:ring-sky-100
                                    disabled:cursor-not-allowed
                                    disabled:bg-slate-100
                                    disabled:opacity-50
                                  "
                                />
                              </div>

                              {/* ACTIONS */}

                              <div className="flex flex-wrap gap-2 lg:ml-auto">
                                {/* OPEN/CLOSE */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleSession(
                                      dayIndex,
                                      sessionIndex,
                                    )
                                  }
                                  className={`
                                    rounded-xl
                                    px-4
                                    py-2.5
                                    text-sm
                                    font-bold
                                    text-white
                                    shadow-sm
                                    transition
                                    hover:-translate-y-0.5

                                    ${
                                      session.isOpen
                                        ? "bg-red-500 hover:bg-red-600"
                                        : "bg-emerald-600 hover:bg-emerald-700"
                                    }
                                  `}
                                >
                                  {session.isOpen
                                    ? "Close"
                                    : "Open"}
                                </button>

                                {/* SAVE SLOT */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    saveSlot(
                                      day,
                                      session,
                                    )
                                  }
                                  disabled={
                                    !session.isOpen ||
                                    isSaving
                                  }
                                  style={
                                    !isSaving &&
                                    session.isOpen
                                      ? gradientButtonStyle
                                      : undefined
                                  }
                                  className="
                                    rounded-xl
                                    px-4
                                    py-2.5
                                    text-sm
                                    font-bold
                                    text-white
                                    shadow-sm
                                    transition
                                    hover:-translate-y-0.5
                                    hover:shadow-md
                                    disabled:cursor-not-allowed
                                    disabled:opacity-50
                                  "
                                >
                                  {isSaving
                                    ? "Saving..."
                                    : "💾 Save Slot"}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>

                  {/* SAVE DAY */}

                  <div className="mt-5 flex justify-end border-t border-slate-100 pt-4">
                    <button
                      type="button"
                      onClick={() =>
                        saveDay(
                          day,
                          dayIndex,
                        )
                      }
                      disabled={
                        savingDay ===
                        dayIndex
                      }
                      style={
                        savingDay !==
                        dayIndex
                          ? gradientButtonStyle
                          : undefined
                      }
                      className="
                        rounded-xl
                        px-5
                        py-2.5
                        font-bold
                        text-white
                        shadow-sm
                        transition
                        hover:-translate-y-0.5
                        hover:shadow-md
                        disabled:cursor-not-allowed
                        disabled:opacity-50
                      "
                    >
                      {savingDay ===
                      dayIndex
                        ? "Saving Day..."
                        : "💾 Save Day"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ====================================================
            FOOTER
        ==================================================== */}

        <div className="py-8 text-center">
          <div
            className="
              mx-auto
              mb-2
              h-1
              w-16
              rounded-full
            "
            style={{
              backgroundImage: BUTTON_GRADIENT,
            }}
          />

          <p className="text-xs font-medium text-slate-400">
            MRHealth · Healthcare Scheduling
            Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default OtherMedicalAvailability;
